<template>
  <div id="app" :class="routeClassName" class="sticky-wrapper" style="z-index: 1">
    <metainfo>
      <template v-slot:title="{ content }">
        {{ content ? `${content}` : $t("LANDING.PAGE_TITLE") }}
      </template>
    </metainfo>

    <loading :active="isLoading" :can-cancel="false" :is-full-page="true" loader="spinner" color="#343a40" :z-index="11000"/>

    <page-loader :is-loading="!isPageLoaded" @loading-complete="onPageLoaded"/>

    <notifications position="bottom center" class="notifications-group"/>

    <div class="page-container" :id="pageContainerId">
      <template v-show="isPageLoaded">
        <web-header
          v-if="!isMobile"
          ref="webHeader"
          v-show="$route.meta.header !== 'hidden'"
        />
        <router-view :style="{ paddingTop: isMobile ? '0px' : contentPaddingTop }"/>
        <web-footer
          v-if="!isMobile"
          v-show="$route.meta.footer !== 'hidden'"
          :style="{ 'padding-bottom': paddingBottom }"
        />
      </template>
    </div>

    <WhatsAppWidget v-if="shouldLoadWhatsAppWidget"/>
    <virtual-balance-bubble v-show="$route.meta.virtual_balance_bubble !== 'hidden'"/>

    <PopupSecurityPin :shouldCheckPin="true"/>
    <PopupVerify2FA/>
    <PopupMaintenance v-if="showMaintenance"/>
    <PurchaseProofNotification/>
    <OnboardingQuiz/>
    <ModalUserConsentHandler/>

    <Popup ref="kycPhase2UpdatePrompt" @on-positive-clicked="openPopupKycPhase2Info"/>
    <PopupKycPhase2Info v-if="hasShownKycPhase2Prompt" ref="popupKycPhase2Info"/>
  </div>
</template>

<script>
import WebHeader from "./layout/ui/Header.vue";
import WebFooter from "./layout/ui/Footer.vue";
import WhatsAppWidget from "./components/WhatsAppWidget";
import VirtualBalanceBubble from "./components/VirtualBalanceBubble.vue";
import { useMeta } from "vue-meta";
import { useI18n } from "vue-i18n";
import { watch } from "vue";
import PageLoader from "@/components/PageLoader.vue";
import PopupSecurityPin from "@/components/PopupSecurityPin.vue";
import PopupVerify2FA from "@/components/PopupVerify2FA.vue";
import PopupMaintenance from "@/components/PopupMaintenance.vue";
import { isAdmin } from "./constants/roles";
import PurchaseProofNotification from "./components/Notifications/PurchaseProofNotification.vue";
import OnboardingQuiz from "./components/Quizzes/OnboardingQuiz.vue";
import { performAction } from "./helpers/common";
import { applyHtmlLang } from "./helpers/seo";
import { CONFIG_REFRESH, STORAGE_ACTION_KEYS } from "./constants/constants";
import ModalUserConsentHandler from "./components/UserConsent/ModalUserConsentHandler.vue";
import Popup from "@/components/Popup.vue";
import PopupKycPhase2Info from "@/components/kycPhase2/PopupKycPhase2Info.vue";
import accountService from "@/services/account.service";

export default {
  components: {
    WebHeader,
    WebFooter,
    WhatsAppWidget,
    VirtualBalanceBubble,
    PageLoader,
    PopupSecurityPin,
    PopupVerify2FA,
    PopupMaintenance,
    PurchaseProofNotification,
    OnboardingQuiz,
    ModalUserConsentHandler,
    Popup,
    PopupKycPhase2Info,
  },

  data() {
    return {
      faviconPath: require("@/assets/img/logo.png"),
      isPageLoaded: false,
      contentPaddingTop: "0px",
      pageContainerId: "",
      hasShownKycPhase2Prompt: false,
      configRefreshTimer: null,
      configRefreshStopped: false,
    };
  },

  async mounted() {
    this.handleWindowResize();
    window.addEventListener("resize", this.handleWindowResize);

    document.onreadystatechange = () => {
      if (document.readyState === "complete") {
        this.onPageLoaded();
      }
    };

    await store.dispatch("refreshSanctumCsrfCookie");
    await store.dispatch("getGeoLocation");
    await store.dispatch("getAdminPermissions");
    const configLoaded = await store.dispatch("refreshConfig");
    this.scheduleConfigRefresh(
      configLoaded ? CONFIG_REFRESH.INTERVAL_MS : CONFIG_REFRESH.RETRY_INTERVAL_MS
    );
    await store.dispatch("refreshUserProfile");
    await store.dispatch("getUnreadNotificationCount");
    await store.dispatch("getInitialBalance");
    await store.dispatch("getExchangeRates");
    await store.dispatch("getPendingTaskCount");
    await store.dispatch("getUnClaimRewardTransactionsCount");

    /**
     * Dispatch get Go learn playlists
     */
    await store.dispatch("getGoLearnPlaylists");

    this.pageContainerId = this.$route.meta.id;
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
    this.configRefreshStopped = true;
    clearTimeout(this.configRefreshTimer);
    this.configRefreshTimer = null;
  },

  computed: {
    shouldLoadWhatsAppWidget() {
      return (
        !window.location.pathname.includes("landing-en") &&
        !window.location.pathname.includes("landing-wa") &&
        !window.location.pathname.includes("landing-wa-2") &&
        !window.location.pathname.includes("login-admin") &&
        !window.location.pathname.includes("admin") &&
        !window.location.pathname.includes("property-image") &&
        !window.location.pathname.includes("event/")
      );
    },
    isLoading() {
      return store.getters.isLoading;
    },
    routeClassName() {
      if (
        this.$route.path &&
        (this.$route.path.includes("admin") ||
          this.$route.path.includes("partner") ||
          this.$route.path.includes("buy-success") ||
          this.$route.path.includes("sharing-assets-overview") ||
          this.$route.path.includes("get-browser-session-id") ||
          this.$route.path.includes("property-image")) ||
        this.$route.path.includes("event/")
      ) {
        return "hide-header-footer";
      }
      return "app";
    },
    paddingBottom() {
      return store.getters.userProfile ? "10px" : "150px";
    },
    isMobile() {
      return this.$route.query.mobile;
    },
    showMaintenance() {
      return !isAdmin() && this.$route.path && !this.$route.path.includes("admin");
    },
    shouldPromptKycPhase2Update() {
      const user = this.$store.getters.userProfile
      const kyc2 = user?.kyc_phase_2;
      return (kyc2?.is_update_required === true && kyc2?.is_displayed_update_popup === false) || this.isRequiredByAdmin;
    },
    isUpdateKycInfo() {
      const kyc2 = this.$store.getters.userProfile?.kyc_phase_2;
      return kyc2?.is_update_required === true && kyc2?.is_displayed_update_popup === false;
    },
    isRequiredByAdmin() {
      const user = this.$store.getters.userProfile
      const kyc2 = user?.kyc_phase_2
      return user?.required_kyc_2 && user && !kyc2
    },
  },

  watch: {
    $route: {
      async handler(to) {
        const shouldShowPopup = !to.path.includes('kyc-phase-2')
        if (shouldShowPopup && this.isRequiredByAdmin) {
          this.$refs.kycPhase2UpdatePrompt?.openPopupWithoutParams()
        }
        this.pageContainerId = to.meta.id;
        if (this.isPageLoaded) {
          performAction(STORAGE_ACTION_KEYS.GET_UNREAD_COUNT, async () => {
            await store.dispatch("getUnreadNotificationCount");
          })
          performAction(STORAGE_ACTION_KEYS.GET_UNCLAIM_REWARD_TRANSACTIONS_COUNT, async () => {
            await store.dispatch("getUnClaimRewardTransactionsCount");
          })
          performAction(STORAGE_ACTION_KEYS.GET_PLAYLISTS, async () => {
            await store.dispatch("getGoLearnPlaylists");
          }, 60 * 1000)
        }
        this.handleWindowResize();
      },
      deep: true,
    },

    shouldPromptKycPhase2Update: {
      immediate: true,
      handler(shouldShow) {
        if (!shouldShow || this.hasShownKycPhase2Prompt) return;
        this.hasShownKycPhase2Prompt = true;

        this.$nextTick(() => {
          this.$refs.kycPhase2UpdatePrompt?.openPopup({
            title: this.isRequiredByAdmin ? this.$t("account.ADDITIONAL_VERIFICATION_REQUIRED") :this.$t("account.PLEASE_UPDATE_YOUR_INFORMATION"),
            message: this.isRequiredByAdmin ? this.$t("account.ADDITIONAL_VERIFICATION_REQUIRED_DESCRIPTION") : this.$t("account.PLEASE_UPDATE_YOUR_INFORMATION_DESCRIPTION"),
            positiveButton: this.$t("AUTH.CONTINUE"),
            negativeButton: this.isRequiredByAdmin ? null : this.$t("common.CANCEL"),
            btnClass: "col-4",
          });
        });
        if (this.isUpdateKycInfo) {
          try {
            accountService.setKycPhase2PopupDisplayed();
            this.markKycPhase2PromptAsShownLocally();
          } catch (e) {
            // swallow; UX already prevented repeat this session
          }
        }
      },
    },
  },

  methods: {
    /**
     * Re-arms itself after every attempt rather than using setInterval: the
     * delay is wall-clock based, so a backgrounded tab (where browsers throttle
     * timers) resumes on schedule instead of drifting, and a failed refresh can
     * back off to the short retry without a separate timer. Self-re-arming also
     * means attempts can never pile up the way a fixed interval can.
     */
    scheduleConfigRefresh(delay) {
      clearTimeout(this.configRefreshTimer);
      if (this.configRefreshStopped) {
        return;
      }
      this.configRefreshTimer = setTimeout(async () => {
        const configLoaded = await store.dispatch("refreshConfig");
        // The dispatch can outlive the component. Without this guard the
        // re-arm below would resurrect a timer beforeDestroy already cleared,
        // leaving it running against a destroyed instance forever.
        if (this.configRefreshStopped) {
          return;
        }
        this.scheduleConfigRefresh(
          configLoaded ? CONFIG_REFRESH.INTERVAL_MS : CONFIG_REFRESH.RETRY_INTERVAL_MS
        );
      }, delay);
    },
    onPageLoaded() {
      this.isPageLoaded = true;
    },
    handleWindowResize() {
      if (this.$refs.webHeader) {
        if (this.routeClassName !== "hide-header-footer") {
          const headerHeight = this.$refs.webHeader.$el.offsetHeight;
          this.contentPaddingTop = `${headerHeight > 80 ? headerHeight : 80}px`;
        } else {
          this.contentPaddingTop = "80px";
        }
      }
    },
    markKycPhase2PromptAsShownLocally() {
      const profile = { ...this.$store.getters.userProfile };
      if (profile?.kyc_phase_2) {
        profile.kyc_phase_2 = {
          ...profile.kyc_phase_2,
          is_displayed_update_popup: true,
        };
        this.$store.dispatch("setUserProfile", profile);
      }
    },
    async openPopupKycPhase2Info() {
      if (this.isRequiredByAdmin) {
        await this.$router.push({ path: "/account/my-account/kyc-phase-2" })
      } else {
        this.$refs.popupKycPhase2Info.openPopup();
      }
    },
  },

  setup() {
    const { t, locale } = useI18n();
    const faviconPath = require("@/assets/img/logo.png");

    const { meta } = useMeta({
      title: t("LANDING.PAGE_TITLE"),
      meta: [
        { name: "description", content: t("LANDING.PAGE_META_DESCRIPTION") },
        { property: "og:title", content: t("LANDING.PAGE_TITLE") },
        { property: "og:site_name", content: "GORO" },
        { property: "og:description", content: t("LANDING.PAGE_META_DESCRIPTION") },
        { name: "twitter:title", content: t("LANDING.PAGE_TITLE") },
        { name: "twitter:description", content: t("LANDING.PAGE_META_DESCRIPTION") },
      ],
      link: [{ rel: "icon", href: faviconPath }],
    });

    // Sync title when locale changes (EN ↔ ID route navigation)
    watch(
      locale,
      (value) => {
        meta.title = t("LANDING.PAGE_TITLE");
        // Keep <html lang> matching the rendered content for crawlers and
        // assistive technologies, including locale changes with no navigation
        // (profile preferred_locale, manual switch, ?lang=).
        applyHtmlLang(value);
      },
      { immediate: true }
    );
  },
};
</script>

<style lang="scss">
.notifications-group {
  bottom: 15px !important;

  span {
    .vue-notification-template.vue-notification.success {
      font-size: 14px;
      text-align: center;
      color: white;
      border: 3px solid var(--primary-color) !important;
      border-radius: 30px;
      background-color: var(--primary-color);
      box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
    }

    .vue-notification-template.vue-notification.error {
      font-size: 14px;
      text-align: center;
      color: #ffffff;
      border: #a82116 !important;
      border-radius: 30px;
      background-color: #a82116;
      box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
    }

    .vue-notification-template.vue-notification.warn {
      font-size: 14px;
      text-align: center;
      color: white;
      border: 3px solid #f5c275 !important;
      border-radius: 30px;
      background-color: #f5c275;
      box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);
    }
  }
}

.grecaptcha-badge {
  right: auto !important;
  left: 10px !important;
  bottom: 10px !important;
  visibility: hidden !important;
}
</style>
